import is from 'is_js';

/**
 * Class which adds / removes Classes from the body, based on a ajax call. To Cachebust the caching plugin.
 */
export default class CacheBusting {
    constructor () {
        // Set default variables.
        this.classes = [
            'is-ie',
            'is-edge',
            'is-chrome',
            'is-firefox',
            'is-opera',
            'is-safari',
            'is-ios',
            'is-iphone',
            'is-ipad',
            'is-ipod',
            'is-android',
            'is-android-phone',
            'is-android-tablet',
            'is-blackberry',
            'is-windows-phone',
            'is-windows-tablet',
            'is-windows',
            'is-mac',
            'is-linux',
            'is-desktop',
            'is-phone',
            'is-tablet',
            'is-touch',
            'is-landscape',
            'is-portrait',
            'no-flex-gap',
        ];

        this.init();
        const ww = window.innerWidth || 0;
        window.addEventListener('resize', () => {
            if( ww !== window.innerWidth) {
                this.init();
            }
        });
        window.addEventListener('orientationchange', () => this.init());
    }

    init () {
        this.windowWidth = window.innerWidth
            || document.documentElement.clientWidth
            || document.body.clientWidth;

        this.windowHeight = window.innerHeight
            || document.documentElement.clientHeight
            || document.body.clientHeight;

        this.addClasses = this.classesToAdd();
        this.removeClasses = this.classesToRemove();

        this.modifyBodyClasses();
    }

    /**
     * Store variable, returns true/false if succeeded.
     * @param key
     * @param value
     * @return {boolean}
     */
    store (key, value) {
        if (undefined === key || undefined === value) {
            return false;
        }
        sessionStorage.setItem(`gkt_${key}__${this.windowWidth}x${this.windowHeight}`, JSON.stringify(value));

        return true;
    }

    /**
     * Return array of stored values based on key.
     * @param key
     * @return {*[]|any}
     */
    get (key) {
        return JSON.parse(sessionStorage.getItem(`gkt_${key}__${this.windowWidth}x${this.windowHeight}`)) || undefined;
    }

    /**
     * Modify body classes, adding and removing those from the array.
     */
    modifyBodyClasses () {
        // Remove classes from the document.body.
        for (const cssClassName of Object.values(this.removeClasses)) {
            if ('string' === typeof cssClassName && document.body.classList.contains(cssClassName)) {
                document.body.classList.remove(cssClassName);
            }
        }

        // Add classes to the document.body.
        for (const cssClassName of Object.values(this.addClasses)) {
            if ('string' === typeof cssClassName && !document.body.classList.contains(cssClassName)) {
                document.body.classList.add(cssClassName);
            }
        }
    }

    /**
     * Return all classes minus the classes which are added.
     * @return {*[]}
     */
    classesToRemove () {
        const bodyClasses = this.get('body_classes_remove');
        if (undefined !== bodyClasses) {
            return bodyClasses;
        }

        // Set difference between 2 arrays.
        const array = [...new Set([
            ...this.classes.filter(x => !this.addClasses.includes(x)),
            ...this.addClasses.filter(x => !this.classes.includes(x)),
        ])];

        // Store in sessionStorage.
        this.store('body_classes_remove', array);

        return array;
    }

    /**
     * source: http://is.js.org/#environment-checks
     */
    classesToAdd () {

        const bodyClasses = this.get('body_classes_add');
        if (undefined !== bodyClasses) {
            return bodyClasses;
        }

        const array = [];

        // Checks if the current browser is ie. Parameter is optional version number of browser.
        if (is.ie()) {
            array.push('is-ie');
        }
        // Checks if the current browser is edge.
        if (is.edge()) {
            array.push('is-edge');
        }

        // Checks if the current browser is chrome.
        if (is.chrome()) {
            array.push('is-chrome');
        }

        // Checks if the current browser is firefox.
        if (is.firefox()) {
            array.push('is-firefox');
        }

        // Checks if the current browser is opera.
        if (is.opera()) {
            array.push('is-opera');
        }

        // Checks if the current browser is safari.
        if (is.safari()) {
            array.push('is-safari');
        }

        // Checks if the current device has ios.
        // true if current device is iPhone, iPad or iPod.
        if (is.ios()) {
            array.push('is-ios');
        }

        // Checks if the current device is iPhone.
        if (is.iphone()) {
            array.push('is-iphone');
        }

        // Checks if the current device is iPad.
        if (is.ipad()) {
            array.push('is-ipad');
        }

        // Checks if the current device is iPod.
        if (is.ipod()) {
            array.push('is-ipod');
        }

        // Checks if the current device has Android.
        if (is.android()) {
            array.push('is-android');
        }

        // Checks if the current device is Android phone.
        if (is.androidPhone()) {
            array.push('is-android-phone');
        }

        // Checks if the current device is Android tablet.
        if (is.androidTablet()) {
            array.push('is-android-tablet');
        }

        // Checks if the current device is Blackberry.
        if (is.blackberry()) {
            array.push('is-blackberry');
        }

        // Checks if the current device is Windows phone.
        if (is.windowsPhone()) {
            array.push('is-windows-phone');
        }

        // Checks if the current device is Windows tablet.
        if (is.windowsTablet()) {
            array.push('is-windows-tablet');
        }

        // Checks if the current OS is Windows.
        if (is.windows()) {
            array.push('is-windows');
        }

        // Checks if the current OS is Mac OS X.
        if (is.mac()) {
            array.push('is-mac');
        }

        // Checks if the current OS is linux.
        if (is.linux()) {
            array.push('is-linux');
        }

        // Checks if the current device is desktop.
        if (is.desktop()) {
            array.push('is-desktop');
        }

        // Checks if the current device is mobile.
        // iPhone, iPod, Android Phone, Windows Phone, Blackberry.
        if (is.mobile()) {
            array.push('is-phone');
        }

        // Checks if the current device is tablet.
        // iPad, Android Tablet, Windows Tablet
        if (is.tablet()) {
            array.push('is-tablet');
        }

        // Checks if the current device supports touch.
        if (is.touchDevice()) {
            array.push('is-touch');
        }

        // Portrait mode.
        if (window.matchMedia('(orientation: portrait)').matches) {
            array.push('is-portrait');
        }

        // Landscape mode.
        if (window.matchMedia('(orientation: landscape)').matches) {
            array.push('is-landscape');
        }

        // Set uniqueClasses (ES6).
        // Quickest way conform: Source: https://stackoverflow.com/questions/1960473/get-all-unique-values-in-a-javascript-array-remove-duplicates
        const uniqueArrayItems = [...new Set(array)];

        // Store in sessionStorage.
        this.store('body_classes_add', uniqueArrayItems);

        return uniqueArrayItems;
    }
}
